<!--
  The keyboard-first grid for an expense, income or transfer batch. A full row is
  completable without the mouse in the order it is read: date, account, counter-account
  (category or contact), amount, memo - date and account carry forward from the row above
  (AGENTS.md), and Enter commits the row and opens the next the moment it is complete,
  from whichever field completed it.

  Rows within one entry: the captain's split requirement. A row starts with one target;
  "+ split" adds another posting line under the same entry, sharing its date and account,
  each with its own target and amount, so a single payment can be divided across several
  categories or contacts with a running total for the entry.

  A row a module recognises (a loan payment, a car cost) gets a detail row beneath it -
  or a side panel, for a long form - holding what that module needs. The grid knows the
  extension interface only, never a module.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { ActionButton, StatusTag } from '@taler/ui'
import { formatLocalDate, type Money, parseLocalDate } from '@taler/domain'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { AccountDelta, BatchCounts, RowOutcome } from '../composables/use-entry-batch.ts'
import { rowCurrency, type FlowKind, type RowDraft } from '../internal/draft.ts'
import type { CoreField, RowView } from '../internal/extension.ts'
import type { RowResolution } from '../internal/row-status.ts'
import type { Merchant } from '../internal/merchant-targets.ts'
import type { TagOption } from '../internal/tag-options.ts'
import type { Frecency, Target } from '../internal/targets.ts'
import AccountCombobox from '@/features/entries/components/AccountCombobox.vue'
import MerchantCombobox from '@/features/entries/components/MerchantCombobox.vue'
import TagsInput from '@/features/entries/components/TagsInput.vue'

const props = defineProps<{
  kind: FlowKind
  rows: RowDraft[]
  moneyTargets: readonly Target[]
  counterTargets: readonly Target[]
  targetsById: ReadonlyMap<string, Target>
  frecency: ReadonlyMap<string, Frecency>
  merchants: readonly Merchant[]
  merchantFrecency: ReadonlyMap<string, Frecency>
  tagSuggestions: readonly TagOption[]
  runningDeltas: readonly AccountDelta[]
  views: ReadonlyMap<string, RowView>
  resolutions: ReadonlyMap<string, RowResolution>
  outcomes: ReadonlyMap<string, RowOutcome>
  counts: BatchCounts
  canSubmit: boolean
  optionHint?: ((target: Target) => string | undefined) | undefined
  isSubmitting: boolean
  hasSubmitError: boolean
  submitErrorDetail: string | undefined
  justPosted: readonly string[]
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const emit = defineEmits<{
  addSplit: [rowId: string]
  removeSplit: [rowId: string, splitId: string]
  removeRow: [rowId: string]
  addRow: []
  commitRow: [rowId: string]
  updateDraft: [rowId: string, draft: unknown]
  retryAttach: [rowId: string]
  submit: []
}>()

const FIELD_LABEL =
  'text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]'

const gridRef = useTemplateRef<HTMLElement>('grid')

const counterGroupLabel = computed(() =>
  t(props.kind === 'TRANSFER' ? 'entries.pickerTransferAccounts' : 'entries.pickerCategories'),
)
const counterLabel = computed(() => t(`entries.counterLabel.${props.kind}`))
const accountLabel = computed(() => t(`entries.accountLabel.${props.kind}`))

/** A currency-pinned target that does not match the row's own currency (AGENTS.md: no
 * client-side FX) - the one split problem worth a field-level word, since the other two
 * (no target, no amount) are just "not typed yet" and need no explanation. */
function currencyMismatch(row: RowDraft, splitId: string): boolean {
  return props.resolutions.get(row.id)?.splitProblems.get(splitId) === 'currency'
}

function resolution(row: RowDraft): RowResolution | undefined {
  return props.resolutions.get(row.id)
}

function carries(row: RowDraft, field: CoreField): boolean {
  const extension = resolution(row)?.extension
  return extension === undefined || extension.carries.has(field)
}

function notCarried(row: RowDraft): string {
  return t('entries.extension.notCarried', { label: resolution(row)?.match?.label ?? '' })
}

function isAwaitingAttach(row: RowDraft): boolean {
  return props.outcomes.get(row.id)?.postedEntry !== undefined
}

function splitAmountParts(row: RowDraft) {
  const status = resolution(row)?.status
  const total = props.views.get(row.id)?.total
  if (total === undefined || (status !== 'ready' && status !== 'needs-details')) return undefined
  return total
}

const panelRowId = ref<string | undefined>(undefined)
const panelRow = computed(() => {
  const row = props.rows.find((candidate) => candidate.id === panelRowId.value)
  const found = row === undefined ? undefined : resolution(row)
  return found?.extension?.placement === 'panel' && found.match !== undefined && found.status !== 'blocked'
    ? { row: row!, resolution: found }
    : undefined
})
const panelRef = useTemplateRef<HTMLElement>('panel')

async function openPanel(rowId: string): Promise<void> {
  panelRowId.value = rowId
  await nextTick()
  panelRef.value
    ?.querySelector<HTMLElement>('input, select, button:not([data-panel-close]), [tabindex]')
    ?.focus()
}

function closePanel(): void {
  const rowId = panelRowId.value
  panelRowId.value = undefined
  if (rowId !== undefined) {
    gridRef.value?.querySelector<HTMLElement>(`[data-row-detail="${rowId}"] [data-open-panel]`)?.focus()
  }
}

watch(panelRow, (current) => {
  if (current === undefined) panelRowId.value = undefined
})

async function focusDetail(rowId: string): Promise<void> {
  await nextTick()
  const detail = gridRef.value?.querySelector(`[data-row-detail="${rowId}"]`)
  detail?.querySelector<HTMLElement>('input, select, button, [tabindex]')?.focus()
}

async function focusField(rowId: string, field: string): Promise<void> {
  await nextTick()
  const selector = `[data-row="${rowId}"][data-field="${field}"]`
  const container = gridRef.value?.querySelector<HTMLElement>(selector)
  const input = container?.matches('input') ? container : container?.querySelector('input')
  input?.focus()
}

function onDateChange(row: RowDraft, value: string): void {
  if (value === '') return
  row.date = parseLocalDate(value)
}

function onCommit(row: RowDraft): void {
  const lengthBefore = props.rows.length
  emit('commitRow', row.id)
  // Only when a new row actually appeared - an incomplete row's Enter must not steal
  // focus back to a field the user is still filling in.
  if (props.rows.length > lengthBefore) {
    void focusField(props.rows[props.rows.length - 1]!.id, 'counter')
  } else if (resolution(row)?.status === 'needs-details') {
    void focusDetail(row.id)
  }
}

function onAddRow(): void {
  const lengthBefore = props.rows.length
  emit('addRow')
  if (props.rows.length > lengthBefore) {
    void focusField(props.rows[props.rows.length - 1]!.id, 'counter')
  }
}

const deltaByAccount = computed(() => new Map(props.runningDeltas.map((d) => [d.accountId, d])))

function deltaLabel(target: Target): { parts: Money; isNegative: boolean } | undefined {
  const delta = deltaByAccount.value.get(target.id)
  if (delta === undefined) return undefined
  return {
    parts: delta.delta,
    isNegative: delta.delta.units < 0n,
  }
}

const touchedAccounts = computed(() =>
  props.moneyTargets.filter((target) => deltaByAccount.value.has(target.id)),
)

const postLabel = computed(() =>
  props.counts.ready === 1 ? t('entries.postOne') : t('entries.postCount', { count: props.counts.ready }),
)

const waiting = computed(() =>
  [
    props.counts.needsDetails > 0 ? t('entries.needsDetailsCount', { count: props.counts.needsDetails }) : '',
    props.counts.blocked > 0 ? t('entries.blockedCount', { count: props.counts.blocked }) : '',
    props.counts.incomplete > 0 ? t('entries.incompleteCount', { count: props.counts.incomplete }) : '',
  ].filter((part) => part !== ''),
)
</script>

<template>
  <div
    ref="grid"
    :data-kind="kind"
    class="grid items-start gap-[var(--space-block)]"
    :class="panelRow === undefined ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-[minmax(0,1fr)_26rem]'"
  >
    <div
      class="min-w-0 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] shadow-[var(--shadow-raised)]"
    >
      <TransitionGroup tag="div" name="entry-row">
        <div v-for="row in rows" :key="row.id" :data-entry-row="row.id">
          <div
            class="grid items-start gap-x-[var(--space-cell)] gap-y-[var(--space-inline)] border-b border-[color:var(--color-border)] p-[var(--space-cell)] grid-cols-12 transition-colors duration-[var(--motion-fast)] focus-within:bg-[color:var(--color-surface-raised)] focus-within:shadow-[inset_3px_0_0_var(--color-focus)]"
            :class="justPosted.includes(row.id) ? 'entry-posted' : undefined"
            :inert="isAwaitingAttach(row) || undefined"
          >
            <div class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-4 lg:col-span-2">
              <span aria-hidden="true" :class="FIELD_LABEL">{{ t('entries.date') }}</span>
              <input
                type="date"
                :value="formatLocalDate(row.date)"
                :aria-label="t('entries.date')"
                :data-row="row.id"
                data-field="date"
                class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
                @change="(event) => onDateChange(row, (event.target as HTMLInputElement).value)"
                @keydown.enter.prevent="onCommit(row)"
              />
            </div>
            <div
              class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-4 lg:col-span-3"
              :data-row="row.id"
              data-field="account"
            >
              <span aria-hidden="true" :class="FIELD_LABEL">{{ accountLabel }}</span>
              <AccountCombobox
                :id="`account-${row.id}`"
                :label="accountLabel"
                :model-value="row.accountId"
                :ledger-id="row.accountLedgerId"
                :targets="moneyTargets"
                :frecency="frecency"
                @update:model-value="
                  (value) => {
                    row.accountId = value
                    row.accountLedgerId = undefined
                  }
                "
                @select="(target) => (row.accountLedgerId = target.ledgerId)"
                @accept="onCommit(row)"
              />
            </div>
            <div
              class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-4 lg:col-span-3"
              :data-row="row.id"
              data-field="counter"
            >
              <span aria-hidden="true" :class="FIELD_LABEL">{{ counterLabel }}</span>
              <AccountCombobox
                :id="`counter-${row.id}-${row.splits[0]!.id}`"
                :label="counterLabel"
                :model-value="row.splits[0]!.targetId"
                :ledger-id="row.splits[0]!.targetLedgerId"
                :targets="counterTargets"
                allow-create-contact
                :accounts-label="counterGroupLabel"
                :frecency="frecency"
                :hint="optionHint"
                @update:model-value="
                  (value) => {
                    row.splits[0]!.targetId = value
                    row.splits[0]!.targetLedgerId = undefined
                  }
                "
                @select="(target) => (row.splits[0]!.targetLedgerId = target.ledgerId)"
                @accept="onCommit(row)"
              />
              <p
                v-if="currencyMismatch(row, row.splits[0]!.id)"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
              >
                {{ t('entries.currencyMismatch') }}
              </p>
            </div>
            <div
              class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-4 lg:col-span-2"
              :data-row="row.id"
              data-field="payee"
            >
              <span aria-hidden="true" :class="FIELD_LABEL">{{ t('entries.payee') }}</span>
              <input
                v-if="!carries(row, 'payee')"
                type="text"
                disabled
                :aria-label="t('entries.payee')"
                :placeholder="t('entries.extension.notCarriedShort')"
                :title="notCarried(row)"
                class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface-sunken)] px-[var(--space-inline)] text-[length:var(--text-step-2)]"
              />
              <MerchantCombobox
                v-else
                :id="`payee-${row.id}`"
                :label="t('entries.payee')"
                :model-value="row.payee"
                :merchants="merchants"
                :frecency="merchantFrecency"
                @update:model-value="(value) => (row.payee = value)"
                @accept="onCommit(row)"
              />
            </div>
            <div class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-4 lg:col-span-2">
              <span aria-hidden="true" :class="FIELD_LABEL">{{ t('entries.amount') }}</span>
              <AmountInput
                v-model="row.splits[0]!.amount"
                :currency="rowCurrency(row, targetsById, moneyTargets)"
                :locale="currentLocale"
                :show-total="false"
                :aria-label="t('entries.amount')"
                :data-row="row.id"
                data-field="amount"
                @keydown.enter.prevent="onCommit(row)"
              />
            </div>
            <div class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-4 lg:col-span-5">
              <span aria-hidden="true" :class="FIELD_LABEL">{{ t('entries.memo') }}</span>
              <input
                v-if="!carries(row, 'memo')"
                type="text"
                disabled
                :aria-label="t('entries.memo')"
                :placeholder="t('entries.extension.notCarriedShort')"
                :title="notCarried(row)"
                class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface-sunken)] px-[var(--space-inline)] text-[length:var(--text-step-2)]"
              />
              <input
                v-else
                v-model="row.memo"
                type="text"
                maxlength="100"
                :aria-label="t('entries.memo')"
                :data-row="row.id"
                data-field="memo"
                class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
                @keydown.enter.prevent="onCommit(row)"
              />
            </div>
            <div class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-8 lg:col-span-5">
              <span aria-hidden="true" :class="FIELD_LABEL">{{ t('entries.tags') }}</span>
              <input
                v-if="!carries(row, 'tags')"
                type="text"
                disabled
                :aria-label="t('entries.tags')"
                :placeholder="t('entries.extension.notCarriedShort')"
                :title="notCarried(row)"
                class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface-sunken)] px-[var(--space-inline)] text-[length:var(--text-step-2)]"
              />
              <TagsInput
                v-else
                :id="`tags-${row.id}`"
                :label="t('entries.tags')"
                :model-value="row.tags"
                :suggestions="tagSuggestions"
                @update:model-value="(value) => (row.tags = [...value])"
              />
            </div>
            <div class="flex items-center justify-end whitespace-nowrap col-span-4 lg:col-span-2 lg:self-end">
              <svg
                v-if="justPosted.includes(row.id)"
                width="18"
                height="18"
                viewBox="0 0 16 16"
                role="img"
                :aria-label="t('entries.posted')"
                class="entry-posted-check text-[color:var(--color-ok)]"
              >
                <path
                  d="M3 8.4 6.4 11.8 13 5.2"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  pathLength="1"
                />
              </svg>
              <template v-else>
                <button
                  v-if="carries(row, 'splits')"
                  type="button"
                  class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
                  @click="emit('addSplit', row.id)"
                >
                  {{ t('entries.addSplit') }}
                </button>
                <button
                  v-if="rows.length > 1"
                  type="button"
                  :aria-label="t('entries.removeRow')"
                  class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger)]"
                  @click="emit('removeRow', row.id)"
                >
                  ×
                </button>
              </template>
            </div>
          </div>
          <TransitionGroup tag="div" name="entry-row">
            <div
              v-for="split in row.splits.slice(1)"
              :key="split.id"
              class="grid items-start gap-x-[var(--space-cell)] gap-y-[var(--space-inline)] border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] p-[var(--space-cell)] grid-cols-12"
              :inert="isAwaitingAttach(row) || undefined"
            >
              <div
                class="flex min-w-0 flex-col gap-[var(--space-hair)] col-span-6"
                :data-row="row.id"
                data-field="counter"
              >
                <AccountCombobox
                  :id="`counter-${row.id}-${split.id}`"
                  :label="counterLabel"
                  :model-value="split.targetId"
                  :ledger-id="split.targetLedgerId"
                  :targets="counterTargets"
                  allow-create-contact
                  :accounts-label="counterGroupLabel"
                  :frecency="frecency"
                  :hint="optionHint"
                  @update:model-value="
                    (value) => {
                      split.targetId = value
                      split.targetLedgerId = undefined
                    }
                  "
                  @select="(target) => (split.targetLedgerId = target.ledgerId)"
                  @accept="onCommit(row)"
                />
                <p
                  v-if="currencyMismatch(row, split.id)"
                  class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
                >
                  {{ t('entries.currencyMismatch') }}
                </p>
              </div>
              <div class="min-w-0 col-span-4">
                <AmountInput
                  v-model="split.amount"
                  :currency="rowCurrency(row, targetsById, moneyTargets)"
                  :locale="currentLocale"
                  :show-total="false"
                  :aria-label="t('entries.amount')"
                  :data-row="row.id"
                  data-field="amount"
                  @keydown.enter.prevent="onCommit(row)"
                />
              </div>
              <div class="flex justify-end col-span-2">
                <button
                  type="button"
                  :aria-label="t('entries.removeSplit')"
                  class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger)]"
                  @click="emit('removeSplit', row.id, split.id)"
                >
                  ×
                </button>
              </div>
            </div>
          </TransitionGroup>
          <div
            v-if="row.splits.length > 1"
            class="flex items-center justify-end gap-[var(--space-cell)] border-b border-[color:var(--color-border)] px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('entries.splitTotal') }}
            <MoneyAmount :value="splitAmountParts(row)!" v-if="splitAmountParts(row)" />
          </div>
          <div
            v-if="resolution(row)?.match !== undefined"
            :data-row-detail="row.id"
            class="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] px-[var(--space-cell)] py-[var(--space-inline)]"
          >
            <div class="flex flex-col gap-[var(--space-inline)]">
              <div class="flex flex-wrap items-center gap-[var(--space-inline)]">
                <span
                  class="rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-inline)] py-[var(--space-hair)] text-[length:var(--text-step-1)] font-medium text-[color:var(--color-text)]"
                >
                  {{ resolution(row)!.match!.label }}
                </span>
                <StatusTag
                  v-for="problem in resolution(row)!.problems"
                  :key="problem.message"
                  :tone="problem.severity === 'blocked' ? 'danger' : 'neutral'"
                  size="sm"
                >
                  {{ problem.message }}
                </StatusTag>
                <button
                  v-if="
                    resolution(row)!.extension!.placement === 'panel' &&
                    resolution(row)!.status !== 'blocked' &&
                    !isAwaitingAttach(row)
                  "
                  type="button"
                  data-open-panel
                  :aria-expanded="panelRowId === row.id"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
                  @click="panelRowId === row.id ? closePanel() : openPanel(row.id)"
                >
                  {{ t('entries.extension.details') }}
                </button>
              </div>
              <component
                :is="resolution(row)!.extension!.detail"
                v-if="
                  resolution(row)!.extension!.placement === 'inline' &&
                  resolution(row)!.extension!.detail !== undefined &&
                  resolution(row)!.status !== 'blocked'
                "
                :match="resolution(row)!.match"
                :draft="resolution(row)!.draft"
                :view="views.get(row.id)"
                @update:draft="(draft: unknown) => emit('updateDraft', row.id, draft)"
              />
              <div
                v-if="outcomes.get(row.id)?.error !== undefined"
                class="flex flex-wrap items-center gap-[var(--space-inline)]"
              >
                <StatusTag tone="danger" role="alert">
                  {{
                    isAwaitingAttach(row)
                      ? t('entries.extension.attachFailed', {
                          label: resolution(row)!.match!.label,
                          detail: outcomes.get(row.id)!.error,
                        })
                      : outcomes.get(row.id)!.error
                  }}
                </StatusTag>
                <button
                  v-if="isAwaitingAttach(row)"
                  type="button"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
                  @click="emit('retryAttach', row.id)"
                >
                  {{ t('entries.extension.retry') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>
      <div
        class="flex flex-wrap items-center gap-x-[var(--space-cell)] gap-y-[var(--space-inline)] p-[var(--space-cell)]"
      >
        <button
          type="button"
          data-add-row
          class="inline-flex min-h-[var(--density-target-min)] items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-sunken)]"
          @click="onAddRow"
        >
          {{ t('entries.addRow') }}
          <kbd
            aria-hidden="true"
            class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface-sunken)] px-[var(--space-tight)] font-mono text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >{{ t('entries.shortcutEnter') }}</kbd
          >
        </button>
        <p data-shortcut-hint class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('entries.addRowHint', { key: t('entries.shortcutEnter') }) }}
        </p>
      </div>
    </div>
    <aside
      v-if="panelRow !== undefined"
      ref="panel"
      :aria-label="panelRow.resolution.match!.label"
      class="flex flex-col gap-[var(--space-cell)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)]"
      @keydown.esc.stop="closePanel"
    >
      <div class="flex items-center justify-between gap-[var(--space-inline)]">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ panelRow.resolution.match!.label }}
        </h2>
        <button
          type="button"
          data-panel-close
          :aria-label="t('entries.extension.closeDetails')"
          class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
          @click="closePanel"
        >
          ×
        </button>
      </div>
      <component
        :is="panelRow.resolution.extension!.detail"
        v-if="panelRow.resolution.extension!.detail !== undefined"
        :match="panelRow.resolution.match"
        :draft="panelRow.resolution.draft"
        :view="views.get(panelRow.row.id)"
        @update:draft="(draft: unknown) => emit('updateDraft', panelRow!.row.id, draft)"
      />
    </aside>
    <div class="col-span-full flex flex-col gap-[var(--space-block)]">
      <section
        :aria-label="t('entries.thisBatch')"
        class="flex flex-wrap items-center gap-x-[var(--space-group)] gap-y-[var(--space-cell)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-group)] py-[var(--space-cell)]"
      >
        <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
          <h2 class="text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text)]">
            {{ t('entries.thisBatch') }}
          </h2>
          <StatusTag v-if="counts.ready > 0" tone="ok" size="sm" class="w-fit">
            {{ t('entries.readyCount', { count: counts.ready }) }}
          </StatusTag>
          <p
            v-if="waiting.length > 0"
            data-batch-waiting
            class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('entries.batchWaits', { waiting: waiting.join(' · ') }) }}
          </p>
          <p
            v-if="counts.ready === 0 && waiting.length === 0"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('entries.noCompleteRows') }}
          </p>
        </div>

        <div v-if="touchedAccounts.length > 0" class="flex min-w-0 flex-1 flex-col gap-[var(--space-hair)]">
          <ul class="flex flex-wrap gap-x-[var(--space-group)] gap-y-[var(--space-inline)]">
            <li
              v-for="target in touchedAccounts"
              :key="target.id"
              class="flex min-w-0 max-w-full items-baseline gap-[var(--space-inline)]"
            >
              <span
                class="min-w-0 truncate text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
              >
                {{ t('entries.deltaFor', { account: target.leaf }) }}
              </span>
              <span class="shrink-0">
                <MoneyAmount
                  :value="deltaLabel(target)!.parts"
                  v-if="deltaLabel(target)"
                  :tone="deltaLabel(target)!.isNegative ? 'negative' : 'positive'"
                />
              </span>
            </li>
          </ul>
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ t('entries.deltaHint') }}
          </p>
        </div>

        <StatusTag v-if="hasSubmitError" tone="danger" role="alert" class="w-full">
          {{ submitErrorDetail ?? t('entries.submitErrorGeneric') }}
        </StatusTag>

        <ActionButton
          size="lg"
          type="button"
          :disabled="isSubmitting || !canSubmit"
          class="ml-auto shrink-0 font-medium"
          @click="emit('submit')"
        >
          {{ isSubmitting ? t('entries.posting') : postLabel }}
        </ActionButton>
      </section>
      <slot name="aside" />
    </div>
  </div>
</template>
